// AXIS ZERO - Audio Manager
// Handles background music and procedural sound effects using Web Audio API

class AudioManager {
  constructor() {
    this.audioContext = null;
    this.musicGainNode = null;
    this.sfxGainNode = null;
    this.masterGainNode = null;
    this.musicBuffer = null;
    this.musicSource = null;
    this.isPlaying = false;
    this.isMuted = this.loadMutePreference();
    this.isInitialized = false;
    this.normalVolume = 0.5;
    this.deathVolume = 0.2;
    this.fadeInterval = null;
  }

  loadMutePreference() {
    try {
      return localStorage.getItem('neonStageRun_muted') === 'true';
    } catch {
      return false;
    }
  }

  saveMutePreference() {
    try {
      localStorage.setItem('neonStageRun_muted', this.isMuted.toString());
    } catch {}
  }

  async init() {
    if (this.isInitialized) return;

    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create gain nodes
      this.masterGainNode = this.audioContext.createGain();
      this.masterGainNode.connect(this.audioContext.destination);
      this.masterGainNode.gain.value = this.isMuted ? 0 : 1;
      
      this.musicGainNode = this.audioContext.createGain();
      this.musicGainNode.connect(this.masterGainNode);
      this.musicGainNode.gain.value = 0;
      
      this.sfxGainNode = this.audioContext.createGain();
      this.sfxGainNode.connect(this.masterGainNode);
      this.sfxGainNode.gain.value = 0.6;
      
      // Preload music
      await this.preloadMusic();
      
      this.isInitialized = true;
    } catch (e) {
      console.warn('[AudioManager] Failed to initialize:', e);
    }
  }

  async preloadMusic() {
    try {
      const response = await fetch('/audio/neon-run.mp3');
      const arrayBuffer = await response.arrayBuffer();
      this.musicBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
    } catch (e) {
      console.warn('[AudioManager] Failed to preload music:', e);
    }
  }

  async resumeContext() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  startMusic() {
    if (!this.isInitialized || !this.musicBuffer || this.isPlaying) return;

    this.musicSource = this.audioContext.createBufferSource();
    this.musicSource.buffer = this.musicBuffer;
    this.musicSource.loop = true;
    this.musicSource.connect(this.musicGainNode);
    this.musicSource.start();
    this.isPlaying = true;

    // Fade in over 0.6 seconds
    this.fadeMusic(this.normalVolume, 0.6);
  }

  stopMusic() {
    if (!this.isPlaying || !this.musicSource) return;

    this.fadeMusic(0, 0.3, () => {
      if (this.musicSource) {
        try {
          this.musicSource.stop();
        } catch {}
        this.musicSource = null;
      }
      this.isPlaying = false;
    });
  }

  fadeMusic(targetVolume, duration, callback) {
    if (!this.musicGainNode) return;

    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
    }

    const startVolume = this.musicGainNode.gain.value;
    const volumeDiff = targetVolume - startVolume;
    const steps = Math.ceil(duration * 60);
    const stepSize = volumeDiff / steps;
    let currentStep = 0;

    this.fadeInterval = setInterval(() => {
      currentStep++;
      const newVolume = startVolume + stepSize * currentStep;
      this.musicGainNode.gain.value = Math.max(0, Math.min(1, newVolume));

      if (currentStep >= steps) {
        clearInterval(this.fadeInterval);
        this.fadeInterval = null;
        this.musicGainNode.gain.value = targetVolume;
        if (callback) callback();
      }
    }, 1000 / 60);
  }

  setDeathVolume() {
    this.fadeMusic(this.deathVolume, 0.15);
  }

  restoreVolume() {
    this.fadeMusic(this.normalVolume, 0.3);
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    if (this.masterGainNode) {
      this.masterGainNode.gain.value = this.isMuted ? 0 : 1;
    }
    this.saveMutePreference();
    return !this.isMuted;
  }

  setMuted(muted) {
    this.isMuted = muted;
    if (this.masterGainNode) {
      this.masterGainNode.gain.value = this.isMuted ? 0 : 1;
    }
    this.saveMutePreference();
  }

  // Procedural SFX using Web Audio API
  playButtonClick() {
    if (!this.isInitialized || this.isMuted) return;
    this.playTone(800, 0.05, 'square', 0.15);
  }

  playStartConfirm() {
    if (!this.isInitialized || this.isMuted) return;
    // Rising tone
    this.playTone(400, 0.08, 'square', 0.2);
    setTimeout(() => this.playTone(600, 0.08, 'square', 0.15), 80);
    setTimeout(() => this.playTone(800, 0.1, 'square', 0.2), 160);
  }

  playJump() {
    if (!this.isInitialized || this.isMuted) return;
    this.playSweep(200, 600, 0.12, 'sine', 0.15);
  }

  playDeath() {
    if (!this.isInitialized || this.isMuted) return;
    // Descending harsh tone
    this.playSweep(400, 100, 0.3, 'sawtooth', 0.25);
    setTimeout(() => this.playNoise(0.2, 0.2), 100);
  }

  playNearMiss() {
    if (!this.isInitialized || this.isMuted) return;
    this.playTone(1200, 0.05, 'sine', 0.1);
  }

  playHighModeEntry() {
    if (!this.isInitialized || this.isMuted) return;
    // Soft tone rise, 120-200ms, minimal synth
    this.playSweep(350, 700, 0.15, 'sine', 0.1);
    setTimeout(() => this.playTone(800, 0.05, 'sine', 0.06), 120);
  }

  playRestart() {
    if (!this.isInitialized || this.isMuted) return;
    this.playTone(600, 0.06, 'square', 0.15);
    setTimeout(() => this.playTone(800, 0.08, 'square', 0.2), 60);
  }

  playTone(frequency, duration, type = 'sine', volume = 0.2) {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.type = type;
    oscillator.frequency.value = frequency;
    oscillator.connect(gainNode);
    gainNode.connect(this.sfxGainNode);

    gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + duration);
  }

  playSweep(startFreq, endFreq, duration, type = 'sine', volume = 0.2) {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(startFreq, this.audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(endFreq, this.audioContext.currentTime + duration);
    oscillator.connect(gainNode);
    gainNode.connect(this.sfxGainNode);

    gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + duration);
  }

  playNoise(duration, volume = 0.1) {
    if (!this.audioContext) return;

    const bufferSize = this.audioContext.sampleRate * duration;
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = this.audioContext.createBufferSource();
    const gainNode = this.audioContext.createGain();

    noise.buffer = buffer;
    noise.connect(gainNode);
    gainNode.connect(this.sfxGainNode);

    gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

    noise.start();
  }
}

// Singleton instance
export const audioManager = new AudioManager();
export default audioManager;
